import { useLayoutEffect, useState } from 'react';

type UseOverflowNavOptions = {
  itemCount: number;
  /** Re-run measurement when labels or item count change. */
  measureKey: string;
};

function computeVisibleCount(
  itemWidths: number[],
  availableWidth: number,
  overflowButtonWidth: number,
): number {
  if (itemWidths.length === 0) {
    return 0;
  }

  const totalWidth = itemWidths.reduce((sum, width) => sum + width, 0);
  if (totalWidth <= availableWidth) {
    return itemWidths.length;
  }

  let count = 0;
  let usedWidth = 0;

  for (let index = 0; index < itemWidths.length; index += 1) {
    const remainingItems = itemWidths.length - index - 1;
    const reserveOverflow = remainingItems > 0 ? overflowButtonWidth : 0;

    if (usedWidth + itemWidths[index] + reserveOverflow <= availableWidth) {
      usedWidth += itemWidths[index];
      count += 1;
    } else {
      break;
    }
  }

  count = Math.max(1, count);

  while (count > 1) {
    const visibleWidth = itemWidths
      .slice(0, count)
      .reduce((sum, width) => sum + width, 0);
    const needsOverflow = count < itemWidths.length;
    const totalUsed = visibleWidth + (needsOverflow ? overflowButtonWidth : 0);

    if (totalUsed <= availableWidth) {
      break;
    }

    count -= 1;
  }

  return count;
}

export function useOverflowNav({
  itemCount,
  measureKey,
}: UseOverflowNavOptions) {
  const [visibleCount, setVisibleCount] = useState(1);
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const [measureEl, setMeasureEl] = useState<HTMLDivElement | null>(null);
  const [overflowMeasureEl, setOverflowMeasureEl] =
    useState<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!containerEl || !measureEl || !overflowMeasureEl || itemCount === 0) {
      setVisibleCount(itemCount);
      return;
    }

    const measure = () => {
      const availableWidth = containerEl.clientWidth;
      const itemElements = Array.from(measureEl.children) as HTMLElement[];
      const itemWidths = itemElements.map((element) => element.offsetWidth);
      const overflowButtonWidth = overflowMeasureEl.offsetWidth;

      if (itemWidths.length > 0 && itemWidths.every((width) => width === 0)) {
        return;
      }

      if (overflowButtonWidth === 0 && itemCount > 1) {
        return;
      }

      setVisibleCount(
        computeVisibleCount(itemWidths, availableWidth, overflowButtonWidth),
      );
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(containerEl);
    resizeObserver.observe(measureEl);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerEl, measureEl, overflowMeasureEl, itemCount, measureKey]);

  return {
    visibleCount: Math.min(visibleCount, itemCount),
    setContainerEl,
    setMeasureEl,
    setOverflowMeasureEl,
  };
}
